import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronDown, ChevronRight, RefreshCw, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LocalIngredient } from "./IngredientsEditor";

function getBaseUrl(): string {
  return import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface NutritionValues {
  calories: number;
  total_fat_g: number;
  saturated_fat_g: number;
  cholesterol_mg: number;
  sodium_mg: number;
  total_carbs_g: number;
  dietary_fiber_g: number;
  sugars_g: number;
  protein_g: number;
}

interface IngredientNutrition extends NutritionValues {
  source: "usda" | "claude" | "unmatched";
  ingredientName?: string;
}

export interface NutritionData {
  id: number;
  recipeId: number;
  perServing: NutritionValues;
  perRecipe: NutritionValues;
  ingredientBreakdown: Record<string, IngredientNutrition>;
  unmatchedIngredients: string[] | null;
  claudeEstimates: unknown;
  servingsUsed: number | null;
  confidence: string | null;
  generatedAt: string | null;
}

interface Props {
  recipeId: number | null;
  ingredients: LocalIngredient[];
  scaleFactor?: number;
  onNutritionLoaded?: (data: NutritionData | null) => void;
}

// ─── FDA Label row ────────────────────────────────────────────────────────────

function LabelRow({
  label,
  value,
  unit,
  bold,
  indent,
  thick,
}: {
  label: string;
  value: number | string;
  unit?: string;
  bold?: boolean;
  indent?: boolean;
  thick?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex justify-between items-baseline py-0.5",
        thick ? "border-t-4 border-black" : "border-t border-black/20",
        indent && "pl-4"
      )}
    >
      <span className={cn("text-sm", bold ? "font-black" : "font-normal")}>
        {label}
      </span>
      <span className={cn("text-sm", bold ? "font-black" : "font-normal")}>
        {typeof value === "number" ? Math.round(value * 10) / 10 : value}
        {unit}
      </span>
    </div>
  );
}

// ─── Confidence badge ─────────────────────────────────────────────────────────

function ConfidenceBadge({ confidence }: { confidence: string | null }) {
  if (confidence === "high") {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300 font-medium">
        High confidence — all ingredients matched
      </span>
    );
  }
  if (confidence === "medium") {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300 font-medium">
        Medium — some ingredients estimated by AI
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 font-medium">
      Low — many ingredients estimated
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function NutritionPanel({
  recipeId,
  ingredients,
  scaleFactor = 1,
  onNutritionLoaded,
}: Props) {
  const [open, setOpen] = useState(false);
  const [nutrition, setNutrition] = useState<NutritionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [view, setView] = useState<"serving" | "recipe">("serving");
  const [error, setError] = useState<string | null>(null);

  const fetchNutrition = useCallback(async () => {
    if (!recipeId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${getBaseUrl()}/api/nutrition/${recipeId}`);
      if (resp.status === 404) {
        setNutrition(null);
        onNutritionLoaded?.(null);
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json() as { nutrition: NutritionData };
      setNutrition(data.nutrition);
      onNutritionLoaded?.(data.nutrition);
    } catch {
      setError("Could not load nutrition data.");
    } finally {
      setLoading(false);
    }
  }, [recipeId, onNutritionLoaded]);

  useEffect(() => {
    setNutrition(null);
    setError(null);
    if (recipeId) fetchNutrition();
  }, [recipeId, fetchNutrition]);

  const handleEstimate = async () => {
    if (!recipeId) return;
    setEstimating(true);
    setError(null);
    try {
      const resp = await fetch(
        `${getBaseUrl()}/api/nutrition/estimate/${recipeId}`,
        { method: "POST" }
      );
      if (!resp.ok) {
        const body = await resp.json() as { error?: string };
        throw new Error(body.error ?? `HTTP ${resp.status}`);
      }
      const data = await resp.json() as { nutrition: NutritionData };
      setNutrition(data.nutrition);
      onNutritionLoaded?.(data.nutrition);
      if (!open) setOpen(true);
    } catch (err) {
      setError(String(err));
    } finally {
      setEstimating(false);
    }
  };

  // Lookup map: ingredient id → display name
  const ingNameMap = new Map<number, string>(
    ingredients
      .filter((i) => i.id !== undefined)
      .map((i) => [
        i.id as number,
        [i.amount, i.unit, i.item].filter(Boolean).join(" "),
      ])
  );

  // Effective nutrition values (scaled for per_recipe if scale ≠ 1)
  function getValues(): NutritionValues | null {
    if (!nutrition) return null;
    if (view === "serving") return nutrition.perServing;
    const pr = nutrition.perRecipe;
    if (scaleFactor === 1) return pr;
    return {
      calories: Math.round(pr.calories * scaleFactor),
      total_fat_g: Math.round(pr.total_fat_g * scaleFactor * 10) / 10,
      saturated_fat_g: Math.round(pr.saturated_fat_g * scaleFactor * 10) / 10,
      cholesterol_mg: Math.round(pr.cholesterol_mg * scaleFactor),
      sodium_mg: Math.round(pr.sodium_mg * scaleFactor),
      total_carbs_g: Math.round(pr.total_carbs_g * scaleFactor * 10) / 10,
      dietary_fiber_g: Math.round(pr.dietary_fiber_g * scaleFactor * 10) / 10,
      sugars_g: Math.round(pr.sugars_g * scaleFactor * 10) / 10,
      protein_g: Math.round(pr.protein_g * scaleFactor * 10) / 10,
    };
  }

  const vals = getValues();
  const servingsDisplay =
    nutrition?.servingsUsed != null
      ? scaleFactor !== 1
        ? `${Math.round(nutrition.servingsUsed * scaleFactor)} servings (${scaleFactor}x)`
        : `${nutrition.servingsUsed} servings`
      : "Unknown servings";

  const breakdown = nutrition?.ingredientBreakdown ?? {};

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header / toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-sm font-medium"
      >
        <span className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-orange-500" />
          Nutrition Estimate
          {nutrition && (
            <span className="text-xs text-muted-foreground font-normal ml-1">
              ~{Math.round(nutrition.perServing.calories)} cal/serving
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {open && (
        <div className="p-4 space-y-4">
          {error && (
            <p className="text-xs text-destructive bg-destructive/10 rounded p-2">{error}</p>
          )}

          {!nutrition && !loading && (
            <div className="text-center py-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                No nutrition estimate yet for this recipe.
              </p>
              <Button
                size="sm"
                onClick={handleEstimate}
                disabled={estimating || !recipeId}
                className="gap-2"
              >
                {estimating ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Estimating…</>
                ) : (
                  <><Zap className="h-3.5 w-3.5" /> Generate Nutrition</>
                )}
              </Button>
            </div>
          )}

          {nutrition && vals && (
            <>
              {/* Per-serving / per-recipe toggle + recalculate */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex border rounded-lg overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => setView("serving")}
                    className={cn(
                      "px-3 py-1.5 transition-colors",
                      view === "serving"
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                  >
                    Per Serving
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("recipe")}
                    className={cn(
                      "px-3 py-1.5 transition-colors border-l",
                      view === "recipe"
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                  >
                    Full Recipe
                  </button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleEstimate}
                  disabled={estimating}
                  className="gap-1 h-7 text-xs"
                >
                  {estimating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  Recalculate
                </Button>
              </div>

              {/* Scale note */}
              {scaleFactor !== 1 && view === "recipe" && (
                <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                  Nutrition scaled to {scaleFactor}x recipe
                </p>
              )}

              {/* FDA-style Nutrition Facts label */}
              <div
                className="border-2 border-black rounded p-3 font-sans max-w-xs"
                style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
              >
                <div className="text-2xl font-black leading-none border-b-4 border-black pb-1 mb-1">
                  Nutrition Facts
                </div>
                <div className="text-xs border-b border-black/30 pb-1 mb-1">
                  {view === "serving" ? `Serving size 1 of ${servingsDisplay}` : servingsDisplay}
                </div>

                <div className="border-t-8 border-black pt-1">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs font-bold">Calories</span>
                    <span className="text-3xl font-black leading-none">
                      {Math.round(vals.calories)}
                    </span>
                  </div>
                </div>

                <div className="border-t-4 border-black pt-1">
                  <div className="text-right text-xs font-bold mb-0.5">% Daily Value*</div>
                  <LabelRow label="Total Fat" value={vals.total_fat_g} unit="g" bold />
                  <LabelRow label="Saturated Fat" value={vals.saturated_fat_g} unit="g" indent />
                  <LabelRow label="Cholesterol" value={vals.cholesterol_mg} unit="mg" bold />
                  <LabelRow label="Sodium" value={vals.sodium_mg} unit="mg" bold />
                  <LabelRow label="Total Carbohydrates" value={vals.total_carbs_g} unit="g" bold />
                  <LabelRow label="Dietary Fiber" value={vals.dietary_fiber_g} unit="g" indent />
                  <LabelRow label="Total Sugars" value={vals.sugars_g} unit="g" indent />
                  <LabelRow label="Protein" value={vals.protein_g} unit="g" bold thick />
                </div>

                <p className="text-[10px] text-muted-foreground mt-2 border-t pt-1">
                  * Daily values not calculated. Estimated values only.
                </p>
              </div>

              {/* Confidence badge */}
              <ConfidenceBadge confidence={nutrition.confidence} />

              {/* Ingredient breakdown table */}
              {Object.keys(breakdown).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">
                    Ingredient breakdown
                  </p>
                  <div className="border rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-medium">Ingredient</th>
                          <th className="text-right px-2 py-1.5 font-medium">Cal</th>
                          <th className="text-right px-2 py-1.5 font-medium">Fat</th>
                          <th className="text-right px-2 py-1.5 font-medium">Carbs</th>
                          <th className="text-right px-2 py-1.5 font-medium">Protein</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {Object.entries(breakdown).map(([ingId, nutr]) => {
                          const name =
                            nutr.ingredientName ??
                            ingNameMap.get(parseInt(ingId, 10)) ??
                            `Ingredient #${ingId}`;
                          return (
                            <tr key={ingId} className="hover:bg-muted/20">
                              <td className="px-2 py-1.5 max-w-[140px]">
                                <span className="truncate block" title={name}>
                                  {name}
                                </span>
                                {nutr.source === "usda" && (
                                  <span className="inline-block mt-0.5 text-[10px] px-1 py-0 rounded bg-green-100 text-green-700 border border-green-200 leading-4">
                                    USDA
                                  </span>
                                )}
                                {nutr.source === "claude" && (
                                  <span className="inline-block mt-0.5 text-[10px] px-1 py-0 rounded bg-purple-100 text-purple-700 border border-purple-200 leading-4">
                                    AI estimate
                                  </span>
                                )}
                                {nutr.source === "unmatched" && (
                                  <span className="inline-block mt-0.5 text-[10px] px-1 py-0 rounded bg-gray-100 text-gray-500 border border-gray-200 leading-4">
                                    ⚠ unmatched
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-right">{Math.round(nutr.calories)}</td>
                              <td className="px-2 py-1.5 text-right">{nutr.total_fat_g}g</td>
                              <td className="px-2 py-1.5 text-right">{nutr.total_carbs_g}g</td>
                              <td className="px-2 py-1.5 text-right">{nutr.protein_g}g</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
